<template>
  <div
    v-if="canDrafts || canArticles"
    class="articles-list"
  >
    <page-header header="Articles">
      <button
        v-if="$login.can('article-drafts.create')"
        class="btn btn-primary"
        @click="$router.push('/articles/create')"
      >
        New Article
      </button>
    </page-header>

    <!-- Drafts articles table -->
    <div
      v-if="canDrafts && (!draftsReady || drafts.length)"
      class="my-4"
    >
      <h2 class="mb-2">
        Draft Articles
      </h2>

      <draft-articles-table
        v-if="draftsReady"
        :value="(v) => { return v.latest_revision }"
        :articles="drafts"
        :is-drafts-list="true"
        @delete="showDeleteDraftModal"
      />

      <div
        v-else
        class="loading-wrapper w-100 py-3 chart-grey-background"
      >
        <loading-icon
          :loading="!draftsReady"
          loading-class="text-center"
        />
      </div>
    </div>

    <!-- Published articles table -->
    <div
      v-if="canArticles"
      class="mt-2 mb-4"
    >
      <div class="d-flex my-2">
        <h2 class="mb-0 align-self-center">
          Published Articles
        </h2>
        <div
          v-if="articlesReady"
          class="ml-auto"
        >
          <search
            @input="updateSearchParam"
          />
        </div>
      </div>
      <articles-table
        v-if="articlesReady"
        :articles="articles"
        :pagination="pagination"
        :is-loading="isLoading"
        @change-page="changePage"
        @show-alerts="showAlerts"
        @delete="showDeleteModal"
      />

      <div
        v-else
        class="loading-wrapper w-100 py-3 chart-grey-background"
      >
        <loading-icon
          :loading="!articlesReady"
          loading-class="text-center"
        />
      </div>
    </div>

    <alerts-modal
      v-if="alertArticleId"
      :article-id="alertArticleId"
    />

    <confirm
      v-if="selectedDraft"
      :name="articleHeader(selectedDraft.latest_revision)"
      @confirmed="removeDraft"
    />

    <confirm
      v-if="selectedArticle"
      :name="articleHeader(selectedArticle)"
      @confirmed="removeArticle"
    />
  </div>
</template>

<script>
import axios from '@/axios'
import gateway from '@/gateway'
import AlertsModal from './AlertsModal'
import ArticlesTable from '@/views/articles/Table'
import DraftArticlesTable from '@/views/articles/DraftTable'
import Search from '@/components/Search'
import Confirm from '@/components/Confirm'
import {successNotification} from '@/notifications'
import login from '@/login'
import mixin from './Mixin'
import gql from 'graphql-tag';

export default {
  name: 'ArticleList',

  mixins: [mixin],

  metaInfo: {
    title: 'Articles',
  },

  components: {
    AlertsModal,
    ArticlesTable,
    Confirm,
    Search,
    DraftArticlesTable,
  },

  data () {
    const params = {
      page: 1,
      perPage: 25,
      // filters: [],
      ...this.$route.query,
    }

    return {
      articles: null,
      isLoading: false,
      pagination: null,
      params,
      drafts: null,
      showDeleted: false,
      alertArticleId: null,
      newlyCreatedArticle: null,
      selectedDraft: null,
      selectedArticle: null,
    }
  },

  created () {
    this.getData()
  },

  computed: {
    draftsReady () {
      return this.drafts !== null
    },

    articlesReady () {
      return this.articles !== null
    },

    canDrafts () {
      return this.$login.can('article-drafts.view') || this.$login.can('article-drafts.view.mine') || this.$login.can('article-drafts.view.channel')
    },

    canArticles () {
      return this.$login.can('articles.view') || this.$login.can('articles.view.mine') || this.$login.can('articles.view.channel')
    },
  },

  watch: {
    showDeleted () {
      const index = this.params.filters.indexOf('with_deleted')
      if (index !== -1) {
        this.params.filters.splice(index, 1)
      } else {
        this.params.filters.push('with_deleted')
      }
    },
  },

  methods: {
    async getData () {
      this.pagination = null
      this.articles = null
      this.drafts = null
      this.isLoading = true

      const promises = []

      if (this.canDrafts) {
        promises.push(axios.get('/articles/drafts'))
        // const {data: drafts} =
        // this.drafts = drafts
      }

      if (this.canArticles) {
        promises.push(this.getArticles())
      }
      const responses = await Promise.all(promises)
      if (this.canDrafts) {
        this.drafts = responses[0].data
      }
      this.isLoading = false
    },

    async getArticles () {
      const channelId = login.channelScopeId()
      const variables = {
        ...this.params,
        channelIds: channelId ? [channelId] : undefined,
      }

      for (const key of Object.keys(variables)) {
        if (typeof variables[key] === 'object' && !!variables[key]) {
          variables[key] = variables[key].map(k => parseInt(k, 10))
        }
        if (typeof variables[key] === 'string' && !['search'].includes(key)) {
          variables[key] = parseInt(variables[key])
        }
      }

      const response = await this.$apollo.query({
        query: gql`query articles($channelIds: [Int!] = null, $regionIds: [Int!] = null, $page: Int, $perPage: Int, $search: String) {
          articles (
            page: $page
            perPage: $perPage
            channelIds: $channelIds
            regionIds: $regionIds
            search: $search
            removeArticlesWithoutAccess: true
            showFutureArticles: true
          ) {
            isSuccess
            meta {
              total
              lastPage
              currentPage
              perPage
            }
            value {
              id
              title
              publishedAt
              channel {
                name
                logo
              }
              leadAnalyst {
                name
                picture {
                  round
                }
              }
              secondaryAnalyst {
                name
                picture {
                  round
                }
              }
              contributors {
                name
                picture {
                  round
                }
              }
              contentType {
                name
                color
                id
              }
            }
          }
        }`,
        variables,
      })

      this.pagination = response.data.articles.meta
      this.articles = response.data.articles.value
    },

    changePage (page = 1) {
      this.params.page = page
      const {params} = this
      history.pushState({}, null, this.$router.resolve({query: {...params, page}}).href)
      this.getArticles()
    },

    updateSearchParam (search = '') {
      if (this.params.search != search) {
        this.params.search = search
        this.params.page = 1
        const {params} = this
        history.pushState({}, null, this.$router.resolve({query: {...params}}).href)
        this.getArticles()
      }
    },

    showAlerts (articleId) {
      this.alertArticleId = articleId
      this.$nextTick(() => this.$modal.show('alerts'))
    },

    showDeleteDraftModal (draft) {
      this.selectedDraft = draft
      this.$nextTick(() => this.$modal.show('confirm-delete'))
    },

    showDeleteModal (article) {
      this.selectedArticle = article
      this.$nextTick(() => this.$modal.show('confirm-delete'))
    },

    selectedChannel (selected) {
      this.params.channel_id = selected ? selected.object.id : null
    },
    restore (article) {
      this.$http.patch('/articles/' + article.id + '/restore')
          .then((response) => {
            this.articles[this.companies.indexOf(article)] = response.data
            this.$notify(successNotification(`Restored ${article.title}.`))
          })
    },

    removeArticle () {
      this.$http.delete(`/articles/${this.selectedArticle.id}`).then(() => {
        this.removedArticle()
      })
    },

    removeDraft () {
      this.$http.delete(`/articles/drafts/${this.selectedDraft.id}`).then(() => {
        this.removedArticle(true)
      })
    },

    removedArticle (draft = false) {
      const data = draft ? this.selectedDraft.latest_revision : this.selectedArticle

      this.$notify(successNotification(`${data.display_title || 'The article'} has been deleted.`))

      if (!draft) {
        this.articles.splice(this.drafts.findIndex(d => d.id === this.selectedArticle.id), 1)
        this.selectedArticle = null
      } else {
        this.drafts.splice(this.drafts.findIndex(d => d._id === this.selectedDraft._id), 1)
        this.selectedDraft = null
      }
    },

    createdArticle (response) {
      this.$modal.hide('create-article')
      this.newlyCreatedArticle = response
    },

    goToCreatedArticle () {
      if (this.newlyCreatedArticle) {
        this.$router.push({
          name: 'articles.draft',
          params: {
            draftId: this.newlyCreatedArticle.data.id,
          },
        })
      }
    },
  },
}
</script>
